/**
 * ModalHTMLGenerator - Generates modal HTML structure for media
 *
 * @module ModalHTMLGenerator
 */

'use strict';

/**
 * Modal HTML Generator Class
 */
export class ModalHTMLGenerator {
    /**
     * Create HTML generator
     * @param {Object} config - Configuration
     */
    constructor(config) {
        this.config = config;
    }

    /**
     * Get localized string with fallback
     * @param {string} key - Locale key
     * @param {string} fallback - Fallback text
     * @returns {string} Localized text
     * @private
     */
    getLocale(key, fallback) {
        if (this.config.locales && this.config.locales[key]) {
            return this.config.locales[key];
        }

        return fallback;
    }

    /**
     * Generate complete modal HTML
     * @param {string} modalId - Modal ID
     * @param {MediaData} mediaData - Media data
     * @param {Object} galleryManager - Gallery manager
     * @returns {string} HTML string
     */
    generateModalHTML(modalId, mediaData, galleryManager) {
        const hasGallery        = galleryManager.hasGallery();
        const galleryItems      = hasGallery ? galleryManager.getGallery(galleryManager.currentGallery) : [];
        const totalItems        = galleryItems.length;
        const closeLabel        = this.getLocale('close', 'Close');
        const fullscreenLabel   = this.getLocale('fullscreen', 'Toggle Fullscreen');
        const prevLabel         = this.getLocale('prev', 'Previous');
        const nextLabel         = this.getLocale('next', 'Next');
        const closeContent      = this.config.closeButtonContent || '&times;';
        const closeButton       = this.config.showCloseButton ? this.generateCloseButton(closeLabel, closeContent) : '';
        const closeAttribute    = this.config.closeAttribute || 'data-basemodal-close';
        const overlay           = this.config.showOverlay ? `<div class="bm-modal__bg" aria-hidden="true" ${closeAttribute}></div>` : '';
        const fullscreenContent = this.config.fullscreenButtonContent || '';
        const counterHtml       = this.config.showCounter ? this.generateCounter(galleryManager.currentIndex, totalItems) : '';

        return `
<div id="${modalId}" class="bm-modal bm-media-modal" aria-hidden="true">
    <div tabindex="-1" class="bm-modal__overlay">
        ${closeButton}
        ${this.generateGalleryNavigation(hasGallery, galleryManager.currentIndex, totalItems, prevLabel, nextLabel, counterHtml)}
        <div role="dialog" class="bm-modal__container bm-modal__container--media" aria-modal="true">
            ${this.generateFullscreenButton(fullscreenLabel, fullscreenContent)}
            <div class="bm-modal__content bm-media-content">
                ${this.generateTitle(mediaData.title)}
                <div class="bm-media-wrapper">
                    ${this.config.renderer.renderMedia(mediaData)}
                </div>
                ${this.generateDescription(mediaData.description)}
            </div>
        </div>
        ${overlay}
    </div>
</div>`;
    }

    /**
     * Generate close button HTML
     * @returns {string} HTML string
     * @private
     */
    generateCloseButton(label, content) {
        const closeAttribute = this.config.closeAttribute || 'data-basemodal-close';
        return `
<button aria-label="${label}"
        class="bm-modal__close"
        ${closeAttribute}>
    ${content}
</button>`;
    }

    /**
     * Generate fullscreen button HTML
     * @returns {string} HTML string or empty
     * @private
     */
    generateFullscreenButton(label, content) {
        if (!this.config.fullscreen) {
            return '';
        }

        return `
<button aria-label="${label}"
        class="bm-modal__fullscreen"
        data-media-fullscreen>
    ${content}
</button>`;
    }

    /**
     * Generate title HTML
     * @param {string} title - Title text
     * @returns {string} HTML string or empty
     * @private
     */
    generateTitle(title) {
        if (!title) {
            return '';
        }

        return `<div class="bm-media-title">${this.escapeHtml(title)}</div>`;
    }

    /**
     * Generate description HTML
     * @param {string} description - Description text
     * @returns {string} HTML string or empty
     * @private
     */
    generateDescription(description) {
        if (!description) {
            return '';
        }

        return `<div class="bm-media-description">${this.escapeHtml(description)}</div>`;
    }

    /**
     * Generate gallery navigation HTML
     * @param {boolean} hasGallery - Has gallery
     * @param {number} currentIndex - Current index
     * @param {number} totalItems - Total items
     * @param {string} prevLabel - Previous label
     * @param {string} nextLabel - Next label
     * @param {string} counterHtml - Counter markup
     * @returns {string} HTML string or empty
     * @private
     */
    generateGalleryNavigation(hasGallery, currentIndex, totalItems, prevLabel, nextLabel, counterHtml) {
        if (!hasGallery) {
            return '';
        }

        return `
<button class="bm-media-nav bm-media-nav__prev" type="button" aria-label="${prevLabel}" data-media-nav="prev" ${currentIndex === 0 ? 'disabled' : ''}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" pointer-events="none">
        <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
</button>
<button class="bm-media-nav bm-media-nav__next" type="button" aria-label="${nextLabel}" data-media-nav="next" ${currentIndex === totalItems - 1 ? 'disabled' : ''}>
    <svg width="24" height="24" viewBox='0 0 24 24' fill='none' pointer-events='none'>
        <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
</button>
${counterHtml}`;
    }

    /**
     * Generate gallery counter HTML
     * @param {number} currentIndex - Current index
     * @param {number} totalItems - Total items
     * @returns {string} HTML string
     * @private
     */
    generateCounter(currentIndex, totalItems) {
        return `<div class="bm-media-counter">
    <span class="bm-media-counter__current">${currentIndex + 1}</span> / <span class="bm-media-counter__total">${totalItems}</span>
</div>`;
    }

    /**
     * Escape HTML
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     * @private
     */
    escapeHtml(text) {
        const div       = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export default ModalHTMLGenerator;
